import React, { SetStateAction, useEffect, useState } from 'react'
import { enableMapSet, produce } from 'immer'

import { Input, InputWrapper, Select } from '@amzn/stencil-react-components/form'
import { Col, Hr, Row } from '@amzn/stencil-react-components/layout'
import { Text } from '@amzn/stencil-react-components/text'

import { JobMetadataSelect } from 'src/components/module-groups/JobMetadataSelect'
import { moduleGroupRenderOption } from 'src/components/module-groups/moduleGroupRenderOption'
import { PECValidationDTO } from 'src/models/dto/module-groups/ModuleGroupValidations'
import {
    FunctionalScoreDTO,
    RuleDTO,
    RuleSubType,
    RuleType,
} from 'src/models/dto/module-groups/PECGroupDTO'
import { ModuleGroupUtility } from 'src/pages/module-groups/ModuleGroupUtility'

export interface PECModuleGroupAddModuleProps {
    pecValidations?: PECValidationDTO
    setPecValidations?: React.Dispatch<SetStateAction<PECValidationDTO | undefined>>
    moduleVersionId?: string
    oldModuleVersionId?: string
    moduleScores: string[]
    functionalScores?: FunctionalScoreDTO[]
    updateFunctionalScores: React.Dispatch<React.SetStateAction<FunctionalScoreDTO[] | undefined>>
    rules: RuleDTO[]
    setRules: React.Dispatch<React.SetStateAction<RuleDTO[]>>
}

enableMapSet()

export const PECModuleGroupAddModule = (props: PECModuleGroupAddModuleProps) => {
    const [selectedModuleScores, setSelectedModuleScores] = useState<string[]>([])

    const {
        moduleScores,
        functionalScores,
        updateFunctionalScores,
        moduleVersionId,
        oldModuleVersionId,
    } = props
    useEffect(() => {
        const alreadySelectedScores: string[] | undefined =
            moduleVersionId &&
            functionalScores?.some((functionalScore) =>
                functionalScore.moduleVersionScoreLabelMap.get(moduleVersionId)
            )
                ? functionalScores?.map(
                      (functionalScore) =>
                          functionalScore.moduleVersionScoreLabelMap.get(moduleVersionId) as string
                  )
                : undefined

        setSelectedModuleScores(alreadySelectedScores ?? [])
    }, [functionalScores, moduleVersionId])

    useEffect(() => {
        if (!(moduleVersionId && oldModuleVersionId && oldModuleVersionId !== moduleVersionId)) {
            return
        }

        updateFunctionalScores(
            produce((fss) => {
                if (!fss?.length) {
                    return
                }
                for (const fs of fss) {
                    const oldLabel = fs.moduleVersionScoreLabelMap.get(oldModuleVersionId)
                    if (oldLabel && moduleScores.includes(oldLabel)) {
                        fs.moduleVersionScoreLabelMap.set(moduleVersionId, oldLabel)
                    }
                }
            })
        )
    }, [moduleScores, moduleVersionId, oldModuleVersionId, updateFunctionalScores])

    const onModuleScoreSelect = (moduleScore: string, index: number) => {
        if (!moduleVersionId) {
            return
        }

        updateFunctionalScores(
            produce((fss) => {
                if (!fss) {
                    return
                }
                fss[index].moduleVersionScoreLabelMap.set(moduleVersionId, moduleScore)
            })
        )
    }

    const addJobMetadataCategories = (categories: RuleType[]) => {
        props.setRules((rules) => {
            const updatedRules = rules.filter((rule) => categories.includes(rule.type))
            const newCategories = categories.filter(
                (category) => !updatedRules.some((rule) => rule.type === category)
            )
            const addedRules = newCategories.map((category) => ({
                type: category,
                subType: RuleSubType.INCLUDE,
                values: [],
            }))
            return [...updatedRules, ...addedRules]
        })
    }

    const setRuleSubType = (ruleType: RuleType, subType: RuleSubType) => {
        props.setRules((rules) =>
            rules.map((rule) => {
                if (rule.type === ruleType) {
                    return {
                        ...rule,
                        subType,
                    }
                }
                return rule
            })
        )
    }

    const addValuesToRule = (ruleType: RuleType, values: string[]) => {
        props.setRules((rules) =>
            rules.map((rule) => {
                if (rule.type === ruleType) {
                    return { ...rule, values }
                }
                return rule
            })
        )
    }

    return (
        <>
            <Text fontWeight='bold'>Presentation Equivalent Configuration</Text>
            <InputWrapper
                id='metadata-categories-wrapper'
                labelText='Metadata Categories'
                error={!!props.pecValidations?.metadataCategories}
                footer={
                    props.pecValidations?.metadataCategories
                        ? props.pecValidations?.metadataCategories
                        : undefined
                }
                required
            >
                {(inputProps) => (
                    <Select
                        dataTestId='metadata-categories-select'
                        {...inputProps}
                        value={props.rules.map((rule) => rule.type)}
                        options={Object.values(RuleType)}
                        onChange={(values: string[]) => {
                            addJobMetadataCategories(values.map((value) => value as RuleType))

                            props.setPecValidations?.((rest) => ({
                                ...rest,
                                metadataCategories: '',
                            }))
                        }}
                        width='70%'
                        isMulti
                    />
                )}
            </InputWrapper>
            <ul>
                {props.rules.map((rule) => (
                    <li key={rule.type}>
                        <Row gridGap='S200'>
                            <JobMetadataSelect
                                ruleType={rule.type}
                                ruleSubType={rule.subType}
                                setValues={addValuesToRule}
                                setRuleSubType={setRuleSubType}
                                editSelectedValues={rule.values}
                                pecValidations={props.pecValidations}
                                setPecValidations={props.setPecValidations}
                            />
                        </Row>
                    </li>
                ))}
            </ul>
            {props.functionalScores &&
                props.functionalScores.length > 0 &&
                !ModuleGroupUtility.isSkippedModule(props.moduleVersionId) && (
                    <>
                        <Hr size='wide' />
                        <Text fontWeight='bold'>Functional Equivalent Configuration</Text>
                        <ul>
                            <Col>
                                {props.functionalScores.map((functionalScore, index) => (
                                    <li key={functionalScore.functionalScoreLabel}>
                                        <Col gridGap='S300'>
                                            <InputWrapper
                                                id={`functional-${index}-wrapper`}
                                                labelText='Functional Score Equivalency'
                                            >
                                                {(inputProps) => (
                                                    <Input
                                                        {...inputProps}
                                                        value={functionalScore.functionalScoreLabel}
                                                        disabled={true}
                                                    />
                                                )}
                                            </InputWrapper>
                                            <InputWrapper
                                                id={`functional-${index}-select`}
                                                labelText='Module Score'
                                                error={
                                                    !!props.pecValidations?.functionalEquivalency?.get(
                                                        functionalScore.functionalScoreLabel
                                                    )
                                                }
                                                footer={
                                                    props.pecValidations?.functionalEquivalency?.get(
                                                        functionalScore.functionalScoreLabel
                                                    )
                                                        ? props.pecValidations?.functionalEquivalency?.get(
                                                              functionalScore.functionalScoreLabel
                                                          )
                                                        : undefined
                                                }
                                                required
                                            >
                                                {(inputProps) => (
                                                    <Select
                                                        {...inputProps}
                                                        dataTestId={`functional-${index}-select`}
                                                        options={
                                                            props.moduleScores &&
                                                            props.moduleScores.length > 0
                                                                ? [
                                                                      '',
                                                                      ...props.moduleScores.filter(
                                                                          (moduleScore) =>
                                                                              !selectedModuleScores.includes(
                                                                                  moduleScore
                                                                              )
                                                                      ),
                                                                  ]
                                                                : []
                                                        }
                                                        isSearchable={true}
                                                        onChange={(selectedScore: string) => {
                                                            onModuleScoreSelect(
                                                                selectedScore,
                                                                index
                                                            )

                                                            if (
                                                                !(
                                                                    props.setPecValidations &&
                                                                    props.pecValidations?.functionalEquivalency?.get(
                                                                        functionalScore.functionalScoreLabel
                                                                    )
                                                                )
                                                            ) {
                                                                return
                                                            }

                                                            props.setPecValidations(
                                                                produce((draft) => {
                                                                    draft?.functionalEquivalency?.set(
                                                                        functionalScore.functionalScoreLabel,
                                                                        ''
                                                                    )
                                                                })
                                                            )
                                                        }}
                                                        placeholder='Select a module Scoring...'
                                                        value={
                                                            props.moduleVersionId
                                                                ? functionalScore.moduleVersionScoreLabelMap.get(
                                                                      props.moduleVersionId
                                                                  )
                                                                : ''
                                                        }
                                                        renderOption={moduleGroupRenderOption}
                                                        noOptionsText={
                                                            props.moduleVersionId
                                                                ? 'The selected module has no more scores'
                                                                : ' Please enter a valid module version id at the top of the page'
                                                        }
                                                        listPositioningStrategy={
                                                            index ===
                                                            (props.functionalScores
                                                                ?.length as number) -
                                                                1
                                                                ? 'fixed'
                                                                : 'absolute'
                                                        }
                                                    />
                                                )}
                                            </InputWrapper>
                                            {index + 1 !== props.functionalScores?.length && (
                                                <Hr size='narrow' />
                                            )}
                                        </Col>
                                    </li>
                                ))}
                            </Col>
                        </ul>
                    </>
                )}
        </>
    )
}
